import {Runner} from './models/runner';
import SearchProvider from './providers/search';
import RunnersProvider from './providers/runners';
import RunProvider from './providers/run';
import RenderProvider from './providers/render';
import initSearchFilters, {SearchFiltersStore} from './store/searchFilters';
import initSearchResults, {SearchResultsStore} from './store/searchResults';
import initRunnersStore, {RunnersStore} from './store/runners';

type EventFromTarget<ElementType> = KeyboardEvent & {target: ElementType};

export default class Search {
    year: number;

    private searchProvider: SearchProvider;

    private runnersProvider: RunnersProvider;

    private runProvider: RunProvider;

    private renderProvider: RenderProvider;

    private searchResults: SearchResultsStore;

    private searchFilters: SearchFiltersStore;

    private favoriteRunners: RunnersStore;

    constructor() {
        this.searchProvider = SearchProvider.getInstance();
        this.runnersProvider = RunnersProvider.getInstance();
        this.runProvider = RunProvider.getInstance();
        this.renderProvider = RenderProvider.getInstance();
        this.searchResults = initSearchResults();
        this.searchFilters = initSearchFilters();
        this.favoriteRunners = initRunnersStore();
    }

    init() {
        this.year = this.runProvider.getLatestYear();
        const {run} = this.favoriteRunners.getState();

        if (run) {
            this.searchFilters.setRun(run);
            const radioOptions = Array.from(this.renderProvider.searchFilterRunOptions) as HTMLInputElement[];
            const radio: HTMLInputElement = radioOptions.find(({value}: HTMLInputElement) => value === run);
            if (radio) {
                radio.checked = true;
            }
        }

        this.renderProvider.searchToggleButton?.addEventListener('click', () => {
            this.initSearch();
        });
    }

    initSearch() {
        this.renderProvider.showLoader();

        this.runnersProvider.fetchRunnersList(this.year)
            .then(() => {
                const {
                    run,
                    query,
                } = this.searchFilters.getState();
                this.setFilteredItems(run, query);
                this.renderSearchResults();
            });

        this.renderProvider.showSearchContext();

        this.renderProvider.searchInput.addEventListener('input', ({target}: EventFromTarget<HTMLInputElement>) => {
            this.searchFilters.setQuery(target.value);
        });

        this.renderProvider.searchFilterRunOptions.forEach((radio) => {
            radio.addEventListener('change', ({target}: EventFromTarget<HTMLInputElement>) => {
                this.searchFilters.setRun(target.value);

                if (this.favoriteRunners.getState().favoriteRunner) {
                    this.favoriteRunners.removeFavoriteRunner();
                }

                this.favoriteRunners.setCurrentRun(target.value);
            });
        });

        this.searchFilters.subscribe(({
            run,
            query,
        }) => {
            this.setFilteredItems(run, query);
            this.renderSearchResults();
        });
    }

    setFilteredItems(run, query) {
        const runners = this.searchProvider
            .filterRunners(this.year, query, run)
            .slice(0, 25);
        this.searchResults.setRunners(runners);
    }

    selectRunner(startNumber) {
        const {runners} = this.searchResults.getState();
        const favoriteRunner = runners.find((runner) => runner.startNumber === startNumber);

        if (favoriteRunner) {
            this.favoriteRunners.setFavoriteRunner(new Runner(favoriteRunner));
            this.renderProvider.searchInput.value = '';
        }
    }

    renderSearchResults() {
        const {runners} = this.searchResults.getState();
        this.renderProvider.renderSearchResultsDropdown(runners, (event: EventFromTarget<HTMLButtonElement>) => {
            this.selectRunner(event.target.dataset.startNumber);
        });

        this.renderProvider.hideLoader();
    }
}
